<template>
  <div class="pb-3">
    <organism-external-qr-access-management
      :filter-list="filterList"
      :visitor-options="visitorOptions"
      :site-access-list="siteAccessList"
      @onUpdateFilter="onUpdateFilter"
      @onUpdateCurrentPageOrPerPage="onUpdateCurrentPageOrPerPage"
      @onSetDefaultFilterData="onSetDefaultFilterData"
      @onUpdateOrderBy="onUpdateOrderBy"
    />
  </div>
</template>

<script>
import { DateTime } from "luxon"
import { mapActions, mapGetters } from "vuex"
import mixins from "@/mixins"
import OrganismExternalQrAccessManagement from "@/components/organisms/portal/external/OrganismQrAccessManagement.vue"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import mixinsDatetime from "@/mixins/mixinsDatetime"
import {
  getExternalSiteAccessList,
  getExternalSiteAccessVisitors,
} from "@/api/module-1/site-access"

export default {
  name: "ExternalQrAccessManagement",
  components: { OrganismExternalQrAccessManagement },
  mixins: [mixins, mixinsObjectHelper, mixinsDatetime],
  data() {
    return {
      filterList: {
        visitors: [],
        siteAccessId: "",
        idcId: "",
        description: "",
        status: [],
        requestDate: {
          start: DateTime.now().startOf("day").toISO(),
          end: DateTime.now().toISO(),
          now: DateTime.now().toISO(),
        },
        isFilterDate: false,
        orderBy: null,
        currentPage: 1,
        totalCount: 1,
        perPage: 10,
      },

      visitorOptions: [],

      siteAccessList: [
        {
          status: "",
          id: {},
          requestStartDate: {},
          requestEndDate: {},
          checkInDate: {},
          checkOutDate: {},
          visitors: [],
          description: "",
        },
      ],
    }
  },

  computed: {
    ...mapGetters("appFilter", ["getExternalSiteAccessFilter"]),
    ...mapGetters("siteAccess", ["getSiteSelect"]),
    ...mapGetters("appPermission", ["getCustomerSitePermissions"]),
  },

  watch: {
    getSiteSelect() {
      this.getSiteAccessListFromApi()
      this.getSiteAccessVisitorsFromApi()
    },
  },

  async mounted() {
    await this.getSiteAccessListFromApi()
    await this.getSiteAccessVisitorsFromApi()
  },

  methods: {
    ...mapActions("appFilter", ["setExternalSiteAccessFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getExternalSiteAccessFilter
      this.filterList = this.deepCopyObject(filterDataFromVuex)
    },

    onUpdateFilter(filter) {
      filter.currentPage = 1
      this.setExternalSiteAccessFilter(filter)
      this.getSiteAccessListFromApi()
    },

    onUpdateCurrentPageOrPerPage(filter) {
      const updatedFilter = {
        ...this.getExternalSiteAccessFilter,
        currentPage: filter.currentPage,
        perPage: filter.perPage,
      }
      this.setExternalSiteAccessFilter(updatedFilter)
      this.getSiteAccessListFromApi()
    },

    onUpdateOrderBy(filter) {
      this.setExternalSiteAccessFilter(filter)
      this.getSiteAccessListFromApi()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        visitors: [],
        siteAccessId: "",
        idcId: "",
        description: "",
        status: [],
        requestDate: {
          start: DateTime.now().startOf("day").toISO(),
          end: DateTime.now().toISO(),
          now: DateTime.now().toISO(),
        },
        isFilterDate: false,
        orderBy: null,
        currentPage: 1,
        totalCount: 1,
        perPage: 10,
      }

      this.setExternalSiteAccessFilter(defaultFilter)
      this.getSiteAccessListFromApi()
    },

    async getSiteAccessListFromApi() {
      this.siteAccessList = []
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = this.filterDecoration()
      await getExternalSiteAccessList(filterData)
        .then(async (response) => {
          this.filterList.totalCount = response.totalCount
          const updatedFilter = {
            ...this.getExternalSiteAccessFilter,
            totalCount: response.totalCount,
          }
          this.setExternalSiteAccessFilter(updatedFilter)
          this.siteAccessList = await Promise.all(
            response.data.map(async (siteAccess) => {
              return {
                status: siteAccess.status,
                id: {
                  siteAccessId: siteAccess.siteAccessId,
                  idcId: siteAccess.idcId?.toString().padStart(8, "0"),
                },
                visitors: siteAccess.visitors.map(
                  (visitor) =>
                    `${visitor.firstname}` + " " + `${visitor.lastname}`
                ),
                requestStartDate: {
                  date: this.setDateTimeFormat(
                    siteAccess.requestStartDate,
                    "dd/MM/yyyy"
                  ),
                  time: this.setDateTimeFormat(
                    siteAccess.requestStartDate,
                    "HH:mm:ss"
                  ),
                },
                requestEndDate: {
                  date: this.setDateTimeFormat(
                    siteAccess.requestEndDate,
                    "dd/MM/yyyy"
                  ),
                  time: this.setDateTimeFormat(
                    siteAccess.requestEndDate,
                    "HH:mm:ss"
                  ),
                },
                checkInDate: {
                  date: this.setDateTimeFormat(
                    siteAccess.checkInDate,
                    "dd/MM/yyyy"
                  ),
                  time: this.setDateTimeFormat(
                    siteAccess.checkInDate,
                    "HH:mm:ss"
                  ),
                },
                checkOutDate: {
                  date: this.setDateTimeFormat(
                    siteAccess.checkOutDate,
                    "dd/MM/yyyy"
                  ),
                  time: this.setDateTimeFormat(
                    siteAccess.checkOutDate,
                    "HH:mm:ss"
                  ),
                },
                description: siteAccess.description,
              }
            })
          )
        })
        .finally(() => this.showLoading(false))
    },

    async getSiteAccessVisitorsFromApi() {
      this.showLoading(true)
      let filterData = this.filterDecoration()
      await getExternalSiteAccessVisitors(filterData)
        .then(async (response) => {
          this.visitorOptions = await Promise.all(
            response.data.visitors.map(async (siteAccess) => {
              return {
                id: siteAccess.accessVisitorId,
                name: siteAccess.firstname + " " + siteAccess.lastname,
                identity: siteAccess.identity,
              }
            })
          )
        })
        .finally(() => this.showLoading(false))
    },

    filterDecoration() {
      return {
        PageSize: this.filterList.perPage,
        PageNumber: this.filterList.currentPage,
        IdcId: this.filterList.idcId || undefined,
        SiteAccessId: this.filterList.siteAccessId || undefined,
        OrderBy: this.filterList.orderBy?.id || undefined,
        Identities:
          this.filterList.visitors?.map((item) => item.identity) || undefined,
        Description: this.filterList.description || undefined,
        Statuses: this.filterList.status.map((item) => item.id) || undefined,
        StartDate: this.filterList.isFilterDate
          ? this.removeTimeZone(this.filterList.requestDate.start)
          : undefined,
        EndDate: this.filterList.isFilterDate
          ? this.removeTimeZone(this.filterList.requestDate.end)
          : undefined,
        SiteIds: this.getCustomerSitePermissions || undefined,
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
